import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Mail, LogOut, Inbox, BarChart3, Loader2, KanbanSquare, Megaphone, ListTodo, Map as MapIcon, CalendarClock } from "lucide-react";
import LeadsPanel from "@/components/admin/LeadsPanel";
import AnalyticsPanel from "@/components/admin/AnalyticsPanel";
import PipelinePanel from "@/components/admin/PipelinePanel";
import CampaignsPanel from "@/components/admin/CampaignsPanel";
import TasksPanel from "@/components/admin/TasksPanel";
import FollowUpsPanel from "@/components/admin/FollowUpsPanel";
import MapPanel from "@/components/admin/MapPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ews_admin_token";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("leads");
  const [leadUpdates, setLeadUpdates] = useState({});
  const [followupDue, setFollowupDue] = useState(0);

  const loadFollowupBadge = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/admin/followups/summary`, { headers: { Authorization: `Bearer ${token}` } });
      setFollowupDue(data.due_count || 0);
    } catch {
      // Badge failure should not block the admin shell.
    }
  }, [token]);

  useEffect(() => {
    loadFollowupBadge();
    const refresh = () => loadFollowupBadge();
    window.addEventListener("ews:followups-changed", refresh);
    const timer = setInterval(refresh, 60000);
    return () => { window.removeEventListener("ews:followups-changed", refresh); clearInterval(timer); };
  }, [loadFollowupBadge]);

  const handleLeadUpdate = (id, patch) => {
    setLeadUpdates((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Welcome back, " + (data.user?.name || "Admin"));
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed. Check your credentials.");
    } finally { setLoading(false); }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); };
  const onExpire = () => { logout(); toast.error("Session expired — please log in again."); };

  const inputCls = "w-full pl-11 pr-4 py-3.5 rounded-xl bg-[#FBFAF8] border border-[#E7E5DF] text-[#0B0F14] placeholder:text-slate-400 focus:outline-none focus:border-emerald-600/60 transition-colors text-sm";

  if (!token) {
    return (
      <section className="min-h-screen grid place-items-center bg-[#0E3B3B] px-5 pt-24 pb-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-emerald-500 text-[#0A2C2C] mx-auto mb-5"><Lock size={26} /></span>
            <h1 className="font-display font-extrabold text-3xl text-white">Admin Dashboard</h1>
            <p className="text-white/60 text-sm mt-2">Leads & analytics — admin sign in required.</p>
          </div>
          <form onSubmit={doLogin} className="bg-white rounded-3xl p-7 shadow-soft space-y-4" data-testid="admin-login-form">
            <div className="relative">
              <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="email" className={inputCls} placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" />
            </div>
            <div className="relative">
              <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="password" className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" />
            </div>
            <button type="submit" disabled={loading} data-testid="admin-login-btn"
              className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-[#0E3B3B] text-white font-bold hover:bg-[#0A2C2C] transition-all disabled:opacity-60">
              {loading ? <><Loader2 size={18} className="animate-spin" /> Signing in…</> : "Sign In"}
            </button>
          </form>
        </div>
      </section>
    );
  }

  const tabCls = (id) => `inline-flex shrink-0 items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${tab === id ? "bg-[#0E3B3B] text-white" : "bg-white border border-[#E7E5DF] text-[#0B0F14] hover:border-emerald-600/50"}`;

  return (
    <section className="min-h-screen bg-[#F0EFEA] pt-24 sm:pt-32 pb-20">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#0E3B3B] text-emerald-300 shrink-0"><Inbox size={20} /></span>
            <h1 className="font-display font-extrabold text-xl sm:text-3xl text-[#0B0F14]">EWS Admin Dashboard</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0B0F14] text-white text-sm font-semibold hover:bg-black transition-colors">
            <LogOut size={15} /> Log Out
          </button>
        </div>

        <div className="flex items-center gap-2 mb-6 sm:mb-8 overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0">
          <button onClick={() => setTab("leads")} data-testid="tab-leads" className={tabCls("leads")}><Inbox size={16} /> Leads</button>
          <button onClick={() => setTab("pipeline")} data-testid="tab-pipeline" className={tabCls("pipeline")}><KanbanSquare size={16} /> Pipeline</button>
          <button onClick={() => setTab("map")} data-testid="tab-map" className={tabCls("map")}><MapIcon size={16} /> Map</button>
          <button onClick={() => setTab("tasks")} data-testid="tab-tasks" className={tabCls("tasks")}><ListTodo size={16} /> Tasks</button>
          <button onClick={() => setTab("followups")} data-testid="tab-followups" className={tabCls("followups")}>
            <CalendarClock size={16} /> Follow-Ups
            {followupDue > 0 && <span className="grid place-items-center min-w-5 h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold" data-testid="tab-followups-badge">{followupDue}</span>}
          </button>
          <button onClick={() => setTab("campaigns")} data-testid="tab-campaigns" className={tabCls("campaigns")}><Megaphone size={16} /> Campaigns</button>
          <button onClick={() => setTab("analytics")} data-testid="tab-analytics" className={tabCls("analytics")}><BarChart3 size={16} /> Analytics</button>
        </div>

        {tab === "leads" && <LeadsPanel token={token} onExpire={onExpire} />}
        {tab === "pipeline" && <PipelinePanel token={token} onExpire={onExpire} onLeadUpdate={handleLeadUpdate} />}
        {tab === "map" && <MapPanel token={token} onExpire={onExpire} leadUpdates={leadUpdates} />}
        {tab === "tasks" && <TasksPanel token={token} onExpire={onExpire} />}
        {tab === "followups" && <FollowUpsPanel token={token} onExpire={onExpire} onChanged={loadFollowupBadge} />}
        {tab === "campaigns" && <CampaignsPanel token={token} onExpire={onExpire} />}
        {tab === "analytics" && <AnalyticsPanel token={token} onExpire={onExpire} />}
      </div>
    </section>
  );
}
